import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { WorkOrderSort } from '../../enums/workOrderSort';
import { WorkOrderStatus } from '../../enums/workOrderStatus';
import { WorkOrderModalMode, WorkOrderPanel } from '../../enums/workOrderView';
import type { MapViewSnapshot } from '../../types/CommonMapTypes';
import { COLUMN_DEFAULT_REM, DEFAULT_SORT } from '../../views/InfrastructureManager/WorkOrders/constants';

const DEFAULT_PANEL = WorkOrderPanel.MAP;

const statusRecord = <T,>(value: T) =>
  Object.fromEntries(Object.values(WorkOrderStatus).map((status) => [status, value])) as Record<WorkOrderStatus, T>;

interface WorkOrderLayoutContextValue {
  activePanel: WorkOrderPanel;
  setActivePanel: (panel: WorkOrderPanel) => void;
  isMapPicking: boolean;
  setMapPicking: (isPicking: boolean) => void;
  columnSorts: Record<WorkOrderStatus, WorkOrderSort>;
  setColumnSort: (status: WorkOrderStatus, sort: WorkOrderSort) => void;
  columnWidthsRef: React.MutableRefObject<Record<WorkOrderStatus, number>>;
  // Camera state kept while the map is unmounted behind Calendar or Configuration.
  mapViewSnapshotRef: React.MutableRefObject<MapViewSnapshot | null>;
  // Null when no work order modal is open; the view / edit / create shell reads mode + id from here.
  workOrderModal: { mode: WorkOrderModalMode; id: number | null } | null;
  openDetail: (id: number) => void;
  openEdit: (id: number) => void;
  openCreate: () => void;
  closeDetail: () => void;
}

const WorkOrderLayoutContext = createContext<WorkOrderLayoutContextValue | null>(null);

// Map picking forces the Map panel without overwriting `activePanel`, so the
// user's previous panel returns when picking ends. List view state lives here so
// the list can unmount during picking and come back exactly as it was.
const WorkOrderLayoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedPanel, setActivePanel] = useState(DEFAULT_PANEL);
  const [isMapPicking, setMapPicking] = useState(false);
  const [columnSorts, setColumnSorts] = useState(() => statusRecord(DEFAULT_SORT));
  // Widths (rem) change per frame during a resize and never drive a render.
  const columnWidthsRef = useRef(statusRecord(COLUMN_DEFAULT_REM));
  const mapViewSnapshotRef = useRef<MapViewSnapshot | null>(null);

  // Work order modal open across the app; shared so the list, calendar and map can all open it.
  const [workOrderModal, setWorkOrderModal] = useState<{ mode: WorkOrderModalMode; id: number | null } | null>(null);
  const openDetail = useCallback((id: number) => setWorkOrderModal({ mode: WorkOrderModalMode.VIEW, id }), []);
  const openEdit = useCallback((id: number) => setWorkOrderModal({ mode: WorkOrderModalMode.EDIT, id }), []);
  const openCreate = useCallback(() => setWorkOrderModal({ mode: WorkOrderModalMode.CREATE, id: null }), []);
  const closeDetail = useCallback(() => setWorkOrderModal(null), []);

  const activePanel = isMapPicking ? WorkOrderPanel.MAP : selectedPanel;

  const setColumnSort = useCallback((status: WorkOrderStatus, sort: WorkOrderSort) => {
    setColumnSorts((prev) => ({ ...prev, [status]: sort }));
  }, []);

  const value = useMemo(
    () => ({
      activePanel,
      setActivePanel,
      isMapPicking,
      setMapPicking,
      columnSorts,
      setColumnSort,
      columnWidthsRef,
      mapViewSnapshotRef,
      workOrderModal,
      openDetail,
      openEdit,
      openCreate,
      closeDetail,
    }),
    [activePanel, isMapPicking, columnSorts, setColumnSort, workOrderModal, openDetail, openEdit, openCreate, closeDetail],
  );

  return <WorkOrderLayoutContext.Provider value={value}>{children}</WorkOrderLayoutContext.Provider>;
};

const useWorkOrderLayout = () => {
  const context = useContext(WorkOrderLayoutContext);
  if (!context) throw new Error('useWorkOrderLayout must be used within WorkOrdersProvider');
  return context;
};

export { WorkOrderLayoutProvider, useWorkOrderLayout };
